import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Loadable from 'react-loadable';
import Router from '../../router';
import Header from '../../components/header';
import KVBreadcrumb from '../../components/breadcrumb';
import Loading from '../../components/loading';
import OffCanvasNavigation, {
  OffCanvasItem
} from '../../components/offCanvasNavigation';
import ApplicationActions from '../../store/application/actions';
import SessionActions from '../../store/session/actions';
import 'font-awesome/css/font-awesome.css';
import './styles.css';
import SupportToolsActions from '../../store/supportTools/actions';
import { supportToolsEnabled } from '../../store/supportTools/selector';
import getRoutes from '../../routes';
import {
  getSessionType,
  canSwitchSessionMode,
  getAvailableSessionModes,
  getReachableCommunitiesFromSession
} from '../../store/session/selectors';
import ScreenErrorBoundary, {
  reportError
} from '../../components/screenErrorBoundary';

const LoadableLogin = Loadable({
  loader: () => import('../login'),
  loading() {
    return <Loading />;
  }
});

const LoadableForgotPassword = Loadable({
  loader: () => import('../forgotPassword'),
  loading() {
    return <Loading legend="Cargando formulario" />;
  }
});

// Igual que styles.scss: desde este ancho el body no scrollea.
const DESKTOP_MIN_WIDTH = 860;

class Application extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandOffCanvas: false
    };
  }
  // En escritorio (>= 860 px) la página no scrollea: cada módulo tiene su
  // scroll. Si algo la corre (un scrollIntoView), la barra de arriba queda
  // fuera de vista hasta recargar: se la vuelve a su lugar.
  componentDidMount() {
    window.addEventListener('scroll', this.keepPageAtTop);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.keepPageAtTop);
  }

  keepPageAtTop = () => {
    if (window.innerWidth >= DESKTOP_MIN_WIDTH && window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  };

  // Y al cambiar de sección, arriba (también en pantallas chicas).
  componentDidUpdate(prevProps) {
    if (
      prevProps.location &&
      this.props.location &&
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }

  /**
   * This will be called with any uncaught error that bubbles up from
   * the component’s children’s lifecycle methods, constructors, or
   * render methods (but not the component’s own errors).
   *
   * Link: https://blog.sentry.io/2017/09/28/react-16-error-boundaries
   */
  componentDidCatch(error, errorInfo) {
    reportError(error, errorInfo);
  }

  handleKeyDown = event => {
    if (event.ctrlKey && event.shiftKey && event.key === 'K') {
      if (this.props.supportToolsEnabled) {
        this.props.disableSupportTools();
      } else {
        const hasAdminProfile = (this.props.userProfiles || []).some(
          profile => profile.type === 'ADMIN'
        );
        if (!hasAdminProfile) return;
        this.props.enableSupportTools();
      }
    }
  };

  componentWillMount() {
    this.props.initializeApp(this.props.store);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    this.props.terminateApp(this.props.isLoggedIn);
  }

  toggleOffCanvas = () => {
    this.setState({
      expandOffCanvas: !this.state.expandOffCanvas
    });
  };

  /**
   * Application loading screen. Rendered before
   * `appHasBooted` is fired.
   */
  renderLoadingScreen() {
    return <section className="application--loading">Cargando</section>;
  }

  renderLogin() {
    return (
      <section className="application">
        <LoadableLogin />
      </section>
    );
  }

  renderForgotPassword() {
    return (
      <section className="application">
        <LoadableForgotPassword />
      </section>
    );
  }

  handleChangeActiveProfile = community => {
    const { updateActiveProfile, history } = this.props;
    updateActiveProfile(community);
    history.push('/');
  };

  handleChangeSessionMode = sessionType => {
    const { updateSessionMode, history } = this.props;
    updateSessionMode(sessionType);
    history.push('/');
  };

  renderApp() {
    const {
      hasValidSession,
      location,
      communities,
      currentCommunity,
      avatar,
      userName,
      logOff,
      supportToolsEnabled,
      routes,
      sessionType,
      availableSessionModes,
      canSwitchMode
    } = this.props;
    const currentPath = location.pathname;

    if (!routes) {
      return this.renderLogin();
    }

    return (
      <section className="application">
        {supportToolsEnabled ? (
          <div className="supportToolsIndicator">
            HERRAMIENTAS DE SOPORTE ACTIVAS
          </div>
        ) : null}
        <Header
          sessionType={sessionType}
          communities={communities}
          currentCommunity={currentCommunity}
          avatar={avatar}
          userName={userName}
          onLogOff={logOff}
          onCommunitySelection={this.handleChangeActiveProfile}
          availableSessionModes={availableSessionModes}
          canSwitchMode={canSwitchMode}
          onSessionModeSelection={this.handleChangeSessionMode}
          supportToolsEnabled={supportToolsEnabled}
        />
        <section className="application__container">
          <OffCanvasNavigation>
            {routes?.map(route =>
              route.navItem ? route.navItem(currentPath) : null
            )}
          </OffCanvasNavigation>
          {/*router personalizado por keenvil */}
          <div className="application__container__content">
            <ScreenErrorBoundary resetKey={currentPath}>
              <Router
                hasValidSession={hasValidSession}
                location={location}
                routes={routes}
              />
            </ScreenErrorBoundary>
          </div>
        </section>
      </section>
    );
  }

  render() {
    const { appHasBooted, hasValidSession, location } = this.props;

    if (!appHasBooted) {
      return this.renderLoadingScreen();
    } else {
      if (hasValidSession) {
        return this.renderApp();
      } else {
        if (location.pathname.includes('forgot-password')) {
          return this.renderForgotPassword();
        } else {
          return this.renderLogin();
        }
      }
    }
  }
}

const mapStateToProps = state => {
  const userProfiles = state.session.user?.profiles || [];

  return {
    appHasBooted: state.application.appHasBooted,
    hasValidSession:
      state.session.user && SessionActions.isValidSession(state.session.user),
    currentCommunity: state.session.profile
      ? state.session.profile.community
      : {},
    communities: getReachableCommunitiesFromSession(state),
    avatar: state.session.user ? state.session.user.avatarUri : null,
    userName: state.session.user
      ? `${state.session.user.firstName} ${state.session.user.lastName}`
      : null,
    userProfiles,
    supportToolsEnabled: supportToolsEnabled(state),
    routes: getRoutes(state),
    sessionType: getSessionType(state),
    availableSessionModes: getAvailableSessionModes(state),
    canSwitchMode: canSwitchSessionMode(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    initializeApp: store => {
      dispatch(ApplicationActions.initializeApp(store));
    },

    terminateApp: isLoggedIn => {
      dispatch(ApplicationActions.terminateApp(isLoggedIn));
    },

    logOff: () => {
      dispatch(SessionActions.logOffUser());
    },

    updateActiveProfile: community => {
      dispatch(SessionActions.updateActiveProfile(community));
    },

    updateSessionMode: sessionType => {
      dispatch(SessionActions.updateSessionMode(sessionType));
    },

    enableSupportTools: () => {
      dispatch(SupportToolsActions.enableSupportTools());
    },

    disableSupportTools: () => {
      dispatch(SupportToolsActions.disableSupportTools());
    }
  };
};

const ConnectedApplication = connect(
  mapStateToProps,
  mapDispatchToProps
)(Application);

// By wrapping Application with withRouter we get the location prop,
// which is then passed down to Router to make it aware of route changes
export default withRouter(ConnectedApplication);

/**
 * 
 *  <OffCanvasItem
                label="Configuracion"
                icon="settings"
                linkTo="/settings"
                isActive={currentPath.includes('settings')}
              />
 */
